/**
 * Component: Cart table
 * ------------------------------------------------------------------------------
 * An event based Ajax cart table.
 *
 * @namespace cartTable
 */

import log from 'salvo-lite/log'

import axios from 'axios'
import { formatMoney } from '@shopify/theme-currency'
import 'mdn-polyfills/Object.entries'

import cssClasses from '../helpers/cssClasses'
import { misc as icons } from '../helpers/svg-map'
import { imageParameters, getProductTitle, percentageSaving } from '../helpers/utils'

class CartTable {
    constructor (theme, elem) {
        this._theme = theme
        this._elem = elem
        this._data = {
            freeGiftProducts: JSON.parse(document.querySelector('[data-free-gift-products]').getAttribute('data-free-gift-products')),
            ...JSON.parse(elem.getAttribute('data-cart-table') || '{}')
        }

        this._selectors = {
            subtotal: '[js-cart-table="subtotal"]',
            totalDiscount: '[js-cart-table="totalDiscount"]',
            quantitySelector: '[data-quantity-selector]',
            lineItem: '[js-ajax-cart="lineItem"]',
            lifestyleProduct: 'lifestyle_product'
        }

        /**
         * DOM node selectors.
         */
        this._nodeSelectors = {
            container: elem,
            subtotal: document.querySelectorAll(this._selectors.subtotal),
            totalDiscount: document.querySelector(this._selectors.totalDiscount)
        }

        /**
         * Instance globals.
         */
        this._timer = {}

        log.debug(this.constructor.name, 'Constructed', this)
    }

    onInit () {
        this._render()
        this._setListener()
        // this._checkReload()
    }

    /**
     * Set listeners.
     */
    _setListener () {
        window.Frame.EventBus.listen(['AjaxCart:itemRemoved', 'AjaxCart:itemUpdated'], () => {
            this._refreshPage()
        })
        window.Frame.EventBus.listen('AjaxCart:removingItem', (item) => this._setLoadingState(item))

        window.Frame.EventBus.listen(['AjaxCart:itemAdded'], () => {
            this._render()
        })
    }

    /**
     * Reload the cart page
     */
    _refreshPage () {
        window.location.reload()
    }

    /**
     * Check if page reload has occured
     */

    _checkReload () {
        const refresh = localStorage.getItem('refreshed')

        if (refresh === 'true') {

        } else {
            this._checkCartItems()
        }
    }

    /**
     * Check number of cart items & their totals
     */
    _checkCartItems () {
        const cartItems = document.querySelectorAll('.ajax-cart__line-item')
        const itemQuantity = document.querySelectorAll('.quantity-selector__input')

        if (cartItems.length === 1 && itemQuantity[0].value === '1') {
            localStorage.setItem('refreshed', 'true')
            this._refreshPage()
        }
    }

    /**
     * Render cart template.
     */
    _render () {
        axios.get('/cart.js')
            .then((response) => {
                this._setCartTemplate(response.data)
                this._constructQuantitySelectors()
                this._updateTotal(response.data)
                this._theme.registerComponents(this._elem)
                window.Frame.EventBus.emit('AjaxCart:reinit')
                return response.data
            })
            .catch((error) => {
                return error
            })
    }

    _setCartTemplate (data) {
        this._elem.innerHTML = `${this._getCartLineItemTemplate(data)}`
    }

    /**
     * Get cart line item template
     * @param {object} data - Cart data.
     */
    _getCartLineItemTemplate (data) {
        let cartHTML = ''
        const freeGiftProductIds = this._data.freeGiftProducts.map(product => parseInt(product.id))
        const noneGiftItems = data.items.filter(item => !freeGiftProductIds.includes(item.id))
        const giftItems = data.items.filter(item => freeGiftProductIds.includes(item.id))

        noneGiftItems.forEach((item, index) => { cartHTML += this._getLineItemTemplate(item, index) })
        giftItems.forEach((item, index) => { cartHTML += this._getLineItemTemplate(item, index, true) })

        return `${cartHTML}`
    }

    /**
     * Get gift item template.
     * @param {object} item - Gift item data object.
     */
    _getLineItemTemplate (item, index, gift = false) {
        let partNumberHTML = ''

        if (item.product_type !== 'ESB') {
            partNumberHTML = `<span class="ajax-cart__sku">${window.theme.strings.partNumber} ${item.sku}</span>`
        }

        return `
            <div class="cart-table__item${gift ? ' cart-table__item-gift' : ''} ajax-cart__line-item" js-ajax-cart="lineItem" data-key="${item.key}">
            ${this._getImageHtml(item)}

            <div class="cart-table__description">
            ${getProductTitle(item)}
            ${partNumberHTML}

            ${this._getItemProperties(item)}
            ${this._getVariantTitle(item)}

            ${this._getQuantitySelector(item, index)}

            ${this._getDeliveryDateHtml(item)}
            </div>

            <div class="cart-table__price-remove">
            <span class="cart-table__prices">${this._getPriceHtml(item)}</span>
            <span class="cart-table__remove ajax-cart__remove" js-ajax-cart="remove" data-key="${item.key}" tabindex="0"><small>${window.theme.strings.cart.remove}</small></span>
            </div>
        </div>
        `
    }

    /**
     * Return item's delivery date html.
     * @param {Object} item The line item JSON object.
     * @returns {String} the item's delivery date html.
     */
    _getDeliveryDateHtml (item) {
        let deliveryHtml = ''
        // const deliveryDatesAttribute = item.product_type === this._selectors.lifestyleProduct ? `data-delivery-dates='{ "beforeCutOffDays": "${window.theme.deliveryDays.lifestyleDeliveryBeforeCutOff}", "afterCutOffDays": "${window.theme.deliveryDays.lifestyleDeliveryAfterCutOff}" }'` : `data-delivery-dates='{ "beforeCutOffDays": "${window.theme.deliveryDays.carPartsDeliveryBeforeCutOff}", "afterCutOffDays": "${window.theme.deliveryDays.carPartsDeliveryAfterCutOff}" }'`

        if ((item.properties !== null && item.properties._installation_bundle) || item.product_type === 'ESB' || item.product_type === 'with Fitting') {
            deliveryHtml = ''
        } else {
            deliveryHtml = `
        <div class="cart-table__estimated-delivery weight--medium">
            ${window.theme.deliveryDays.deliveryTitle}: <span>${localStorage.getItem(`${item.handle}`)}</span>
        </div>
        `
        }

        return deliveryHtml
    }

    /**
     * Output the image HTML.
     * @param {Object} item line item data object.
     * @returns {String} the image HTML element.
     */
    _getImageHtml (item) {
        let imageHtml = ''

        if (item.image === null) {
            return imageHtml
        }

        imageHtml = '<div class="cart-table__image responsive-image__wrapper">'

        if (item.product_type !== 'ESB') {
            imageHtml += `<a class="cart-table__image-link" href="${item.url}">`
        }

        imageHtml += `
            <img
            src="${imageParameters(item.image, {size: '200x200', crop: 'center'})}"
            srcset="${imageParameters(item.image, {size: '120x120', crop: 'center'})} 120w, ${imageParameters(item.image, {size: '240x240', crop: 'center'})} 240w, ${imageParameters(item.image, {size: '320x320', crop: 'center'})} 320w"
            alt="${item.title}"
            >

            ${this._getInstallationBanner(item)}`

        if (item.product_type !== 'ESB') {
            imageHtml += '</a>'
        }

        imageHtml += '</div>'

        return imageHtml
    }

    /**
     * Return installation banner.
     * @param {Object} item The line item JSON object.
     * @returns {String} installation HTML.
     */
    _getInstallationBanner (item) {
        let installationBanner = ''

        if (item.properties === null) {
            return installationBanner
        }

        if (item.properties._installation_bundle) {
            installationBanner = `<div class="ajax-cart__banner">${window.theme.strings.cart.drawer.installationIncluded}</div>`
        }

        return installationBanner
    }

    /**
     * Get the line item properties (used by ESB plan products).
     * @param {Object} item line item data object.
     */
    _getItemProperties (item) {
        if (!item.properties) {
            return ''
        }

        let properties = ''
        Object.entries(item.properties).forEach((entry) => {
            const key = entry[0]
            const value = entry[1]

            properties += (
                key !== 'vehicleCode' &&
                key !== 'engineCode' &&
                key !== 'emissionCode'
            ) ? `<span class="cart-table__property"><span class="weight--medium">${window.theme.strings.servicePlans.cart[key]}:</span> ${value}</span>` : ''
        })

        if (item.properties && item.properties.address) {
            return `
        <div class="cart-table__properties">
            <h2 class="cart-table__property-title weight--medium">${window.theme.strings.servicePlans.cart.planDetails}</h2>

            ${properties}
        </div>
        `
        } else {
            return ''
        }
    }

    /**
     * Get variant titles.
     * @param {object} item - Line item data object.
     * @returns {String} Variant title HTML.
     */
    _getVariantTitle (item) {
        if (item.variant_options.length && !item.variant_options[0].includes('Default Title')) {
            let variantTitle = this._checkForDefaultVariant(item.variant_title)
            return `<p class="meta">${variantTitle}</p>`
        } else {
            return ''
        }
    }

    /**
     * Check for default variant titles.
     * @param {object} title - Variant title.
     * @returns {String} Variant class.
     */
    _checkForDefaultVariant (title) {
        if (title.includes('DEFAULT: ')) {
            title = title.split('DEFAULT: ')[1]
        }
        return title
    }

    /**
     * Get quantity selector.
     * @param {Object} item the line item object.
     * @param {Number} index the index of the forEach.
     * @returns {String} quantity HTML.
     */
    _getQuantitySelector (item, index) {
        if (item.product_type === 'ESB') {
            return ''
        }

        return `
        <div class="cart-table__quantity-selector" data-label="Quantity">
        <label for="Quantity-${index}" class="label">Quantity</label>
        <div id="QuantitySelector-${index}" class="quantity-selector quantity-selector--small" js-cart-table="quantitySelector" data-quantity-selector='{ "callback": "cart-table", "id": ${item.id} }' data-installation="${this._getInstallationClass(item)}">
            <div class="quantity-selector__control">
            <button type="button" class="button quantity-selector__button" data-quantity-selector-decrement value="decrement">
                ${icons.minus}
                <span class="visually-hidden">-</span>
            </button>
            </div>

            <div class="quantity-selector__control">
            <input type="number" id="Quantity-${index}" name="quantity" value="${item.quantity}" min="1" pattern="[0-9]*" class="quantity-selector__input" data-quantity-selector-input readonly />
            </div>

            <div class="quantity-selector__control ${this._getInstallationClass(item)}">
            <button type="button" class="button quantity-selector__button" js-quantity-selector="increment" value="increment" data-quantity-selector-increment>
                ${icons.plus}
                <span class="visually-hidden">+</span>
            </button>
            </div>
        </div>
        </div>
    `
    }

    /**
     * Output the class 'quantity-selector__control--installation' to identify
     * items in the cart that need installation to style the quantity selector.
     * @param {Object} item line item data object.
     */
    _getInstallationClass (item) {
        let installationClass = ''

        if (item.properties === null) {
            return installationClass
        }

        if (item.properties._installation_bundle) {
            installationClass = 'quantity-selector__control--installation'
        }

        return installationClass
    }

    /**
     * Return price HTML based on parameters.
     * @param {Object} item The line item JSON object.
     * @returns {String} Price HTML.
     */
    _getPriceHtml (item) {
        let priceHtml = `<span class="price">${formatMoney(item.price, window.theme.moneyFormat)}</span>`

        if (item.properties === null) {
            return priceHtml
        }

        if (item.properties._compare_at_price) {
            const percentageOff = percentageSaving(item.price, item.properties._compare_at_price)
            let discountHtml = `<span class="visually-hidden">${window.theme.strings.originalPrice}</span>
            <span class="price price--compare-at"><s>${formatMoney(item.properties._compare_at_price, window.theme.moneyFormat)}</s></span>
            <span class="price price--percentage-off">(${percentageOff}${window.theme.strings.percentageOff})</span>`

            if (percentageOff <= 0) discountHtml = ''

            priceHtml = `
            <span class="visually-hidden">${window.theme.strings.discountedPrice}</span>
            <span class="price">${formatMoney(item.price, window.theme.moneyFormat)}</span>
            ${discountHtml}`
        }

        return priceHtml
    }

    /**
     * Instantiate quantity selectors using id.
     * Callback has a timer to reduce number of calls.
     */
    _constructQuantitySelectors () {
        const quantitySelectors = Array.from(document.querySelectorAll(this._selectors.quantitySelector))

        quantitySelectors.forEach((item) => {
            item.addEventListener('quantitySelectorInitialised', e => {
                this._handleQuantityEvent(item)
            })
        })
    }

    /**
     * Handle quantity updates.
     * @param {HTMLElement} element - Quantity selector node element.
     */
    async _handleQuantityEvent (element) {
        const data = {
            key: element.closest(this._selectors.lineItem).dataset.key,
            quantity: parseInt(element.querySelector('input').value, 10)
        }

        let variantID = data.key.split(':')

        try {
            const payload = {
                'updates': {
                    [variantID[0]]: data.quantity
                }
            }

            const cart = await axios.post('/cart/update.js', payload)
            this._updateSubtotal(cart.data.total_price)
            this._updateDiscountTotal(cart.data.total_discount)
            window.Frame.EventBus.emit('AjaxCart:itemUpdated')
        } catch (error) {
            console.log(error)
        }
    }

    /**
     * Update subtotal and discount total.
     * @param {object} data - Cart response data.
     */
    _updateTotal (data) {
        this._updateSubtotal(data.total_price)
        this._updateDiscountTotal(data.total_discount)
    }

    /**
     * Update subtotal.
     * @param {Number} price - Total cart price number.
     */
    _updateSubtotal (price) {
        [...this._nodeSelectors.subtotal].forEach((element) => {
            element.innerHTML = formatMoney(price, window.theme.moneyFormat)
        })
    }

    /**
     * Update discount total.
     * @param {Number} price - Total discount price number.
     */
    _updateDiscountTotal (price) {
        if (!this._isCartDiscounted(price)) {
            this._hideDiscountLabel()
            return
        }
        this._nodeSelectors.totalDiscount.innerHTML = formatMoney(price, window.theme.moneyFormat)
    }

    /**
     * Set loading state.
     * @param {object} element - Line item row in DOM.
     */
    _setLoadingState (element) {
        element.classList.add(cssClasses.removing)
    }

    /**
     * Check discount total.
     * @param {Number} total - Total discount price number.
     */
    _isCartDiscounted (total) {
        return total > 0
    }

    /**
     * Hide discount label.
     */
    _hideDiscountLabel () {
        this._nodeSelectors.totalDiscount.parentElement.classList.add(cssClasses.hidden)
    }

    /**
     * Show discount label.
     */
    _showDiscountLabel () {
        this._nodeSelectors.totalDiscount.parentElement.classList.remove(cssClasses.hidden)
    }

    // return Object.freeze({
    //   init,
    // })
}

export default CartTable



// WEBPACK FOOTER //
// ./src/resources/scripts/components/CartTable.js